import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import type { ComplementoInput, GrupoComplementos, GrupoInput } from '../types';

interface Props {
  open: boolean;
  grupo: GrupoComplementos | null;
  onClose: () => void;
  onSubmit: (input: GrupoInput) => Promise<void> | void;
  loading?: boolean;
}

interface FormRow extends ComplementoInput {
  _key: string;
}

const key = () => Math.random().toString(36).slice(2);

export function GrupoFormModal({ open, grupo, onClose, onSubmit, loading }: Props) {
  const [nome, setNome] = useState('');
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(1);
  const [obrigatorio, setObrigatorio] = useState(false);
  const [ativo, setAtivo] = useState(true);
  const [itens, setItens] = useState<FormRow[]>([]);

  useEffect(() => {
    if (!open) return;
    setNome(grupo?.nome ?? '');
    setMin(grupo?.min_selecao ?? 0);
    setMax(grupo?.max_selecao ?? 1);
    setObrigatorio(grupo?.obrigatorio ?? false);
    setAtivo(grupo?.ativo ?? true);
    setItens(
      (grupo?.complementos ?? []).map((c) => ({
        _key: key(),
        id: c.id,
        nome: c.nome,
        preco_adicional: c.preco_adicional,
        ordem: c.ordem,
        ativo: c.ativo,
      })),
    );
  }, [open, grupo]);

  const addItem = () => {
    setItens((prev) => [
      ...prev,
      { _key: key(), nome: '', preco_adicional: 0, ordem: prev.length, ativo: true },
    ]);
  };

  const updateItem = (k: string, patch: Partial<FormRow>) => {
    setItens((prev) => prev.map((i) => (i._key === k ? { ...i, ...patch } : i)));
  };

  const removeItem = (k: string) => {
    setItens((prev) => prev.filter((i) => i._key !== k));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: GrupoInput = {
      nome: nome.trim(),
      min_selecao: min,
      max_selecao: max,
      obrigatorio,
      ativo,
      complementos: itens
        .filter((i) => i.nome.trim())
        .map((i, idx) => ({
          id: i.id,
          nome: i.nome.trim(),
          preco_adicional: Number(i.preco_adicional) || 0,
          ordem: idx,
          ativo: i.ativo ?? true,
        })),
    };
    await onSubmit(payload);
  };

  const canSubmit = nome.trim().length > 0 && max >= min;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={grupo ? 'Editar grupo de complementos' : 'Novo grupo de complementos'}
      description="Define opcoes adicionais selecionaveis nos produtos (frutas, caldas, recheios, etc)."
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button
            type="submit"
            form="grupo-form"
            className="btn-primary"
            disabled={loading || !canSubmit}
          >
            {loading ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <form id="grupo-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Nome do grupo</label>
          <input
            type="text"
            className="input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex: Frutas, Caldas, Cereais"
            maxLength={80}
            autoFocus
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Min. selecao
            </label>
            <input
              type="number"
              className="input"
              value={min}
              min={0}
              onChange={(e) => setMin(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Max. selecao
            </label>
            <input
              type="number"
              className="input"
              value={max}
              min={1}
              onChange={(e) => setMax(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
              checked={obrigatorio}
              onChange={(e) => setObrigatorio(e.target.checked)}
            />
            Obrigatorio
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
              checked={ativo}
              onChange={(e) => setAtivo(e.target.checked)}
            />
            Ativo
          </label>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-700">Complementos</h3>
            <button
              type="button"
              onClick={addItem}
              className="inline-flex items-center gap-1 text-sm text-brand-700 hover:text-brand-800"
            >
              <Plus className="h-4 w-4" /> Adicionar
            </button>
          </div>

          {itens.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 py-6 text-center text-sm text-slate-500">
              Nenhum complemento. Clique em &quot;Adicionar&quot; para incluir.
            </p>
          ) : (
            <div className="space-y-2">
              {itens.map((item) => (
                <div
                  key={item._key}
                  className="grid grid-cols-12 gap-2 rounded-lg border border-slate-200 p-2"
                >
                  <input
                    className="input col-span-7 sm:col-span-7"
                    placeholder="Nome"
                    value={item.nome}
                    onChange={(e) => updateItem(item._key, { nome: e.target.value })}
                    maxLength={80}
                  />
                  <div className="col-span-4 sm:col-span-4">
                    <div className="flex items-center rounded-lg border border-slate-300 bg-white px-2">
                      <span className="text-xs text-slate-500">R$</span>
                      <input
                        className="w-full bg-transparent py-2 pl-1 text-sm outline-none"
                        type="number"
                        step="0.01"
                        min={0}
                        value={item.preco_adicional}
                        onChange={(e) =>
                          updateItem(item._key, {
                            preco_adicional: Number(e.target.value),
                          })
                        }
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item._key)}
                    className="col-span-1 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
                    aria-label="Remover"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}
